import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePlayer } from "@/context/PlayerContext";
import { profileImage } from "@/lib/images";
import { ChevronLeft, Upload, Save, Loader2, Play, Pause, Trash2, RefreshCw, Plus, GripVertical, Check } from "lucide-react";

const CATEGORIES = ["Commercial", "Animation", "Character", "Narration", "E-learning", "Gaming"];
const VOK_TAGS = ["Vrolijk & spontaan", "Lief & zacht", "Stoer & energiek", "Nieuwsgierig & eigenwijs", "Fris & naturel", "Schattig & speels", "Rustig & helder", "Expressief & enthousiast", "Droog & grappig", "Warm & oprecht"];

// Show stored date in NL notation for the editable text field
const fmtNL = (s) => {
  if (!s) return "";
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    let [, y, a, b] = m;
    if (parseInt(a, 10) > 12 && parseInt(b, 10) <= 12) [a, b] = [b, a];
    return `${b}-${a}-${y}`;
  }
  m = s.match(/^(\d{4})-(\d{2})$/);
  if (m) return `${m[2]}-${m[1]}`;
  m = s.match(/^(\d{4})$/);
  if (m) return m[1];
  return s;
};

// Parse flexible user input (jjjj | mm-jjjj | dd-mm-jjjj, "/" or "-") into a stored value
const parseDateInput = (raw) => {
  const s = (raw || "").trim();
  if (!s) return null;
  let m = s.match(/^(\d{4})$/); if (m) return m[1];
  m = s.match(/^(\d{1,2})[-/](\d{4})$/); if (m) return `${m[2]}-${m[1].padStart(2, "0")}`;
  m = s.match(/^(\d{4})[-/](\d{1,2})$/); if (m) return `${m[1]}-${m[2].padStart(2, "0")}`;
  m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/); if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/); if (m) return `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
  return null;
};

function DemoTitle({ demo, onSave }) {
  const [val, setVal] = React.useState(demo.title || "");
  React.useEffect(() => { setVal(demo.title || ""); }, [demo.title]);
  const save = () => {
    const v = val.trim();
    if (!v || v === (demo.title || "")) { setVal(demo.title || ""); return; }
    onSave(v);
  };
  return (
    <Input value={val} onChange={(e) => setVal(e.target.value)} onBlur={save}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      placeholder="Demo titel" data-testid={`demo-title-${demo.id}`} className="h-9 w-44 rounded-lg" />
  );
}

function DemoDate({ demo, onSave }) {
  const initial = fmtNL(demo.source_date || demo.uploaded_at);
  const [val, setVal] = React.useState(initial);
  React.useEffect(() => { setVal(fmtNL(demo.source_date || demo.uploaded_at)); }, [demo.source_date, demo.uploaded_at]);
  const save = () => {
    if (val === initial) return;
    const parsed = parseDateInput(val);
    if (parsed) { onSave(parsed); }
    else { toast.error("Ongeldige datum. Gebruik jjjj of dd-mm-jjjj."); setVal(initial); }
  };
  return (
    <Input value={val} onChange={(e) => setVal(e.target.value)} onBlur={save}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      placeholder="jjjj of dd-mm-jjjj" data-testid={`demo-date-${demo.id}`} className="h-9 w-32 rounded-lg" />
  );
}

export default function AdminTalentEdit() {
  const { id } = useParams();
  const player = usePlayer();
  const [t, setT] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoRef = useRef();

  const load = () => api.get(`/admin/talents/${id}`).then((r) => setT(r.data)).catch(() => toast.error("Not found"));
  useEffect(() => { load(); }, [id]);

  if (!t) return <div className="flex justify-center py-20 text-brand-violet"><Loader2 className="animate-spin" /></div>;

  const set = (k, v) => setT((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { tags } = t;
      const payload = { ...t, tags: Array.isArray(tags) ? tags : String(tags || "").split(",").map((s) => s.trim()).filter(Boolean) };
      await api.put(`/admin/talents/${id}`, payload);
      toast.success("Saved");
      load();
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingPhoto(true);
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "photos");
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/admin/talents/${id}`, { photo_url: data.url });
      toast.success("Photo updated"); load();
    } catch (err) { toast.error("Upload failed"); } finally { setUploadingPhoto(false); }
  };

  const tagStr = Array.isArray(t.tags) ? t.tags.join(", ") : (t.tags || "");

  return (
    <div className="max-w-4xl">
      <Link to="/admin/talents" className="inline-flex items-center gap-1 text-sm font-semibold text-zinc-500 hover:text-brand-violet"><ChevronLeft size={16} /> Back to voices</Link>
      <div className="mt-3 flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold">{t.name}</h1>
        <button onClick={save} disabled={saving} data-testid="save-talent" className="inline-flex items-center gap-2 rounded-full bg-brand-violet px-6 py-2.5 text-sm font-bold text-white hover:bg-brand-violetHover disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
        </button>
      </div>

      <div className="mt-6 grid gap-6 md:grid-cols-[220px_1fr]">
        <div>
          <div className="aspect-square overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-100">
            <img src={profileImage(t)} alt="Standaard profielafbeelding" className="h-full w-full object-cover" data-testid="talent-avatar" />
          </div>
          <p className="mt-2 text-center text-xs text-zinc-500">Standaardafbeelding op basis van geslacht ({t.gender === "female" ? "meisje" : "jongen"})</p>
          <div className="mt-4 space-y-3 rounded-xl border border-zinc-200 bg-white p-4">
            <ToggleRow label="Published" val={t.published} onChange={(v) => set("published", v)} testid="edit-published" />
            <ToggleRow label="Featured" val={t.featured} onChange={(v) => set("featured", v)} testid="edit-featured" />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <F label="Public name"><Input value={t.name} onChange={(e) => set("name", e.target.value)} data-testid="edit-name" className="rounded-xl h-11" /></F>
          <F label="Voice type">
            <Select value={t.voice_type} onValueChange={(v) => set("voice_type", v)}>
              <SelectTrigger className="rounded-xl h-11"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="human">Human</SelectItem><SelectItem value="ai">AI</SelectItem></SelectContent>
            </Select>
          </F>
          <F label="Language"><Input value={t.language || ""} onChange={(e) => set("language", e.target.value)} className="rounded-xl h-11" /></F>
          <F label="Country"><Input value={t.country || ""} onChange={(e) => set("country", e.target.value)} className="rounded-xl h-11" /></F>
          <F label="Accent"><Input value={t.accent || ""} onChange={(e) => set("accent", e.target.value)} className="rounded-xl h-11" /></F>
          <F label="Gender">
            <Select value={t.gender} onValueChange={(v) => set("gender", v)}>
              <SelectTrigger className="rounded-xl h-11"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="male">Boy</SelectItem><SelectItem value="female">Girl</SelectItem></SelectContent>
            </Select>
          </F>
          <F label="Age min"><Input type="number" value={t.age_min ?? ""} onChange={(e) => set("age_min", e.target.value ? parseInt(e.target.value) : null)} className="rounded-xl h-11" /></F>
          <F label="Age max"><Input type="number" value={t.age_max ?? ""} onChange={(e) => set("age_max", e.target.value ? parseInt(e.target.value) : null)} className="rounded-xl h-11" /></F>
          <div className="sm:col-span-2">
            <F label="VoiceOverKids Tags (meerdere mogelijk)">
              <div className="grid gap-2 sm:grid-cols-2">
                {VOK_TAGS.map((tag) => {
                  const cur = Array.isArray(t.tags) ? t.tags : String(t.tags || "").split(",").map((s) => s.trim()).filter(Boolean);
                  const checked = cur.includes(tag);
                  const toggle = () => set("tags", checked ? cur.filter((x) => x !== tag) : [...cur, tag]);
                  return (
                    <label key={tag} data-testid={`vok-tag-${tag}`}
                      className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm transition ${checked ? "border-brand-violet bg-violet-50 font-semibold text-zinc-900" : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300"}`}>
                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 ${checked ? "border-brand-violet bg-brand-violet text-white" : "border-zinc-300"}`}>
                        {checked && <Check size={13} />}
                      </span>
                      <input type="checkbox" checked={checked} onChange={toggle} className="sr-only" />
                      {tag}
                    </label>
                  );
                })}
              </div>
            </F>
          </div>
          <div className="sm:col-span-2"><F label="Description"><Textarea rows={3} value={t.description || ""} onChange={(e) => set("description", e.target.value)} className="rounded-xl" /></F></div>
        </div>
      </div>

      <DemoManager talent={t} reload={load} player={player} />
    </div>
  );
}

function F({ label, children }) { return <div className="space-y-1.5"><Label className="label-eyebrow text-zinc-500">{label}</Label>{children}</div>; }
function ToggleRow({ label, val, onChange, testid }) {
  return <div className="flex items-center justify-between"><span className="text-sm font-semibold text-zinc-700">{label}</span><Switch checked={!!val} onCheckedChange={onChange} data-testid={testid} /></div>;
}

function DemoManager({ talent, reload, player }) {
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(null);
  const fileRefs = useRef({});
  const newRef = useRef();
  const [newTitle, setNewTitle] = useState("");
  const [newCat, setNewCat] = useState("Commercial");

  const uploadFile = async (file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "demos");
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  const addDemo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setAdding(true);
    try {
      const up = await uploadFile(file);
      await api.post(`/admin/talents/${talent.id}/demos`, { title: newTitle || file.name.replace(/\.[^.]+$/, ""), category: newCat, url: up.url, source_date: up.source_date, language: talent.language });
      toast.success("Demo added"); setNewTitle(""); reload();
    } catch (err) { toast.error("Upload failed"); } finally { setAdding(false); e.target.value = ""; }
  };

  const replaceDemo = async (demoId, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(demoId);
    try {
      const up = await uploadFile(file); // new unique object -> avoids CDN cache
      await api.put(`/admin/talents/${talent.id}/demos/${demoId}`, { url: up.url, source_date: up.source_date });
      toast.success("Audio replaced"); reload();
    } catch (err) { toast.error("Replace failed"); } finally { setBusy(null); e.target.value = ""; }
  };

  const updateDemo = async (demoId, fields) => { await api.put(`/admin/talents/${talent.id}/demos/${demoId}`, fields); reload(); };
  const delDemo = async (demoId) => { if (!window.confirm("Delete this demo?")) return; await api.delete(`/admin/talents/${talent.id}/demos/${demoId}`); reload(); };

  return (
    <div className="mt-10">
      <h2 className="font-heading text-lg font-bold">Audio demos</h2>
      <div className="mt-4 flex flex-wrap items-end gap-3 rounded-xl border border-dashed border-zinc-300 bg-white p-4">
        <div className="flex-1 min-w-[160px]"><Label className="label-eyebrow text-zinc-500">New demo title</Label><Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="e.g. Commercial demo" className="rounded-xl h-10 mt-1" /></div>
        <div><Label className="label-eyebrow text-zinc-500">Category</Label>
          <Select value={newCat} onValueChange={setNewCat}><SelectTrigger className="rounded-xl h-10 mt-1 w-40"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        </div>
        <input ref={newRef} type="file" accept="audio/*" hidden onChange={addDemo} data-testid="add-demo-input" />
        <button onClick={() => newRef.current?.click()} disabled={adding} data-testid="add-demo-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-violet px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-violetHover disabled:opacity-60">
          {adding ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Upload demo
        </button>
      </div>

      <div className="mt-4 space-y-2">
        {(talent.demos || []).map((d) => {
          const key = `${talent.id}:${d.id}`;
          const active = player.isActive(key);
          return (
            <div key={d.id} data-testid={`admin-demo-${d.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-zinc-200 bg-white p-3">
              <GripVertical size={16} className="text-zinc-300" />
              <button onClick={() => player.playTrack({ key, name: talent.name, sub: d.title, url: mediaUrl(d.url), photo: profileImage(talent), talentId: talent.id })}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-violet text-white">
                {active && player.playing ? <Pause size={15} className="fill-white" /> : <Play size={15} className="fill-white ml-0.5" />}
              </button>
              <DemoTitle demo={d} onSave={(v) => updateDemo(d.id, { title: v })} />
              <Select value={d.category} onValueChange={(v) => updateDemo(d.id, { category: v })}><SelectTrigger className="h-9 w-36 rounded-lg"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              <label className="flex items-center gap-1.5 text-xs text-zinc-600" title="Datum van de opname — typ een jaartal (2024) of volledige datum (dd-mm-jjjj)">
                <span className="text-zinc-400">Datum</span>
                <DemoDate demo={d} onSave={(v) => updateDemo(d.id, { source_date: v })} />
              </label>
              <label className="flex items-center gap-1.5 text-xs text-zinc-600"><Switch checked={d.published} onCheckedChange={(v) => updateDemo(d.id, { published: v })} /> Published</label>
              <div className="ml-auto flex items-center gap-1">
                <input ref={(el) => (fileRefs.current[d.id] = el)} type="file" accept="audio/*" hidden onChange={(e) => replaceDemo(d.id, e)} data-testid={`replace-input-${d.id}`} />
                <button onClick={() => fileRefs.current[d.id]?.click()} disabled={busy === d.id} data-testid={`replace-demo-${d.id}`} className="inline-flex items-center gap-1 rounded-lg border border-zinc-300 px-3 py-1.5 text-xs font-semibold hover:border-brand-violet">
                  {busy === d.id ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Replace
                </button>
                <button onClick={() => delDemo(d.id)} className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>
              </div>
            </div>
          );
        })}
        {(talent.demos || []).length === 0 && <p className="text-sm text-zinc-400">No demos yet. Upload the first one above.</p>}
      </div>
    </div>
  );
}
